import { DrEntity } from './dr-entity';
import { ViewType } from './view';
import { DrEntityAttribute } from './dr-entity-attribute';
import { SearchType } from './enums';
import { isNullOrUndefined } from './utils';

export enum TaskType {
    SEARCH = 'SEARCH',                                   // basic search
    SEARCH_BY_DOCUMENT = 'SEARCH_BY_DOCUMENT',           // search based on a document model
    SEARCH_BY_PROFILE = 'SEARCH_BY_PROFILE',             // search based on a document profile
    SEARCH_FOR_DUPLICATE_DOCUMENTS = 'SEARCH_FOR_DUPLICATE_DOCUMENTS',  // search identifing dupes based on file hash, content hash, or similarity threshold
    VARIATIONS_REPORT = 'VARIATIONS_REPORT',             // search includes variations on search terms
    OTHER = 'OTHER',
    VIEW_MANIFEST_DOWNLOAD = 'VIEW_MANIFEST_DOWNLOAD',
    EXPORT_STREAM_CUSTODIAN_UPDATE_DOWNLOAD = 'EXPORT_STREAM_CUSTODIAN_UPDATE_DOWNLOAD',
    PROJECT_DELETE = 'PROJECT_DELETE',
    COMPARE_EXPORT = 'COMPARE_EXPORT',                    // compare search against export stream
    NEAR_DUPE = 'NEAR_DUPE',                               // find near duplicates search on a document
    DOWNLOAD_DOCUMENTS = 'DOWNLOAD_DOCUMENTS',
    GENERATE_CLUSTER_REPORT = 'GENERATE_CLUSTER_REPORT',
    DOMAIN_COMMUNICATION = 'DOMAIN_COMMUNICATION',
    EMAIL_COMMUNICATION = 'EMAIL_COMMUNICATION'
}

export enum TaskStatus {
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
    IN_PROGRESS = 'IN_PROGRESS',
    CANCELLED = 'CANCELLED',
    INVALID = 'INVALID',
    PENDING_CANCELLATION = 'PENDING_CANCELLATION',
    INVALID_RESULTS = 'INVALID_RESULTS',
    DELETED_RESULTS = 'DELETED_RESULTS',
    PAUSED = 'PAUSED',
    PAUSED_ON_FAILURE = 'PAUSED_ON_FAILURE'
}

export enum TaskStatusTitle {
    SUCCESS = 'Success',
    WARNING = 'Complete with Warnings/Errors',
    RUNNING = 'Running',
    QUEUED = 'Queued',
    BLOCKED = 'Blocked',
    PAUSING = 'Pausing',
    PAUSED = 'Paused',
    PENDING_CANCELLATION = 'Pending Cancellation',
    CANCELLED = 'Cancelled',
    PAUSED_ON_FAILURE = 'Paused on Failure',
    FAILURE = 'Failure',
    UNKNOWN = 'Unknown',
    SUCCESS_VIEW_DOC_LIST = 'Success. Double-click to view the document list',
    WARNING_VIEW_DOC_LIST = 'Warning. Double-click to view the document list'
}

export enum OperationState {
    SUCCESS = 'SUCCESS',
    WARNING = 'WARNING',
    RUNNING = 'RUNNING',
    QUEUED = 'QUEUED',
    BLOCKED = 'BLOCKED',
    PAUSING = 'PAUSING',
    PAUSED = 'PAUSED',
    FAILURE = 'FAILURE',
    UNKNOWN = 'UNKNOWN',
    CANCELLED = 'CANCELLED'
}

export function getIconClassFromTaskStatus(taskStatus: TaskStatus, operationState: OperationState, description = null, isDownloadType = false, isSearchType = false): string {
    const iconClasses = {
        'fa-check': (taskStatus === TaskStatus.SUCCESS &&
            operationState === OperationState.SUCCESS) || (taskStatus === TaskStatus.SUCCESS && description && description.indexOf('OCR_CANDIDATES')),
        'fa-download': isDownloadType,
        'fa-exclamation-triangle': taskStatus === TaskStatus.SUCCESS &&
            operationState === OperationState.WARNING,
        'fa-play': taskStatus === TaskStatus.IN_PROGRESS &&
            operationState === OperationState.RUNNING,
        'fa-clock-o': taskStatus === TaskStatus.IN_PROGRESS &&
            operationState === OperationState.QUEUED,
        'fa-ban': operationState === OperationState.BLOCKED,
        'fa-pause': operationState === OperationState.PAUSED || ((operationState === OperationState.SUCCESS || operationState === OperationState.RUNNING) && taskStatus === TaskStatus.PAUSED),
        'fa-close': taskStatus === TaskStatus.CANCELLED,
        'fa-pause-circle': taskStatus === TaskStatus.PAUSED_ON_FAILURE,
        'fa-exclamation-circle': taskStatus === TaskStatus.FAILURE,
        'fa-pause pausing-cancellation': operationState === OperationState.PAUSING,
        'fa-close pausing-cancellation': taskStatus === TaskStatus.PENDING_CANCELLATION,
        'fa-question': operationState === OperationState.UNKNOWN,
        'fa-list': isSearchType &&
            (
                ((taskStatus === TaskStatus.SUCCESS && operationState === OperationState.SUCCESS) || (taskStatus === TaskStatus.SUCCESS && description && description.indexOf('OCR_CANDIDATES'))) ||
                (taskStatus === TaskStatus.SUCCESS && operationState === OperationState.WARNING)
            ),
    };
    const props = Object.getOwnPropertyNames(iconClasses).filter(p => iconClasses[p]);
    if (props.length > 0) {
        return props[props.length - 1];
    }
    return null;
}

export function getIconTitleForIconClass(iconClass: string, operationState: OperationState): string {
    let iconTitle = '';
    switch (iconClass) {
        case 'fa-check': iconTitle = 'Success'; break;
        case 'fa-exclamation-triangle': iconTitle = 'Complete with Warnings/Errors'; break;
        case 'fa-play': iconTitle = 'Running'; break;
        case 'fa-clock-o': iconTitle = 'Queued'; break;
        case 'fa-ban': iconTitle = 'Blocked'; break;
        case 'fa-pause': iconTitle = 'Paused'; break;
        case 'fa-close': iconTitle = 'Cancelled'; break;
        case 'fa-pause-circle': iconTitle = 'Paused on Failure'; break;
        case 'fa-exclamation-circle': iconTitle = 'Failure'; break;
        case 'fa-pause pausing-cancellation': iconTitle = 'Pausing'; break;
        case 'fa-close pausing-cancellation': iconTitle = 'Pending Cancellation'; break;
        case 'fa-question': iconTitle = 'Unknown'; break;
        case 'fa-download': iconTitle = 'Success - Click to download'; break;
        case 'fa-list': iconTitle = operationState === OperationState.SUCCESS || isNullOrUndefined(operationState) ? 'Success. Double-click to view the document list' : 'Warning. Double-click to view the document list'; break;
    }
    return iconTitle;
}

export class Task extends DrEntity {
    drWsStatus: any;
    taskStatus: TaskStatus;
    extendedStatus: string;
    percentComplete: number;
    numberResults: number;
    description: string;
    viewHandle: string;
    dateStarted: string;
    dateCompleted: string;
    secondsElapsed: number;
    owner: string;
    task: string;
    taskType: TaskType;
    tagList: any[];
    currentStatus: any[];
    operationState: OperationState;
    compareExportList: any[];
    responseObject: any;
    warningDetails: any;
    warnings: boolean;
    viewType: ViewType;

    sampleSetCount: number;
    iconClass: string;
    iconTitle: string;
    isDownloadableTask: boolean;
    isSearchableTask: boolean;

    isTimeoutTask = false;

    constructor(value?: Partial<Task>) {
        super(value);
        if (value) {
            if (value.attributes) {
                const samplesetcount = value.attributes.find(a => a.name === 'samplesetcount'),
                    viewType = value.attributes.find(a => a.name === 'viewtype');
                if (samplesetcount) {
                    this.sampleSetCount = parseInt(samplesetcount.value, 10);
                }
                if (viewType) {
                    this.viewType = <ViewType>viewType.value;
                }
            }
            Object.assign(this, value);

            this.isDownloadableTask = this.isDownloadType();
            this.isSearchableTask = this.isSearchType();
            this.iconClass = getIconClassFromTaskStatus(this.taskStatus, this.operationState, this.description, this.isDownloadableTask, this.isSearchableTask);
            this.iconTitle = getIconTitleForIconClass(this.iconClass, this.operationState);
        }
        if (this.isSearchType && this.description) {
            if (this.description.startsWith('Searching for ')) {
                this.description = this.description.replace('Searching for ', '');
            } else if (this.description.trim().startsWith('Searching ')) {
                this.description = this.description.trim().replace('Searching ', '');
            }
        }
        if ((this.task === TaskType.DOMAIN_COMMUNICATION) || (this.task === TaskType.EMAIL_COMMUNICATION)) {
            this.description = this.description.indexOf('Communication Matrix') != -1 ? this.description.replace('Communication Matrix', 'Communication Grid')
                : this.description.replace('Domain Matrix', 'Domain Grid');
            if (this.description.indexOf('CaseData') != -1) {
                this.description = this.description.replace('CaseData', 'Project Data');
            }
            if (this.description.indexOf('AllCorpora') != -1) {
                this.description = this.description.replace('AllCorpora', 'Imports');
            }
        }
    }

    isSearchType(): boolean {
        return (this.taskType === TaskType.SEARCH) ||
            (this.taskType === TaskType.SEARCH_BY_DOCUMENT) ||
            (this.taskType === TaskType.SEARCH_FOR_DUPLICATE_DOCUMENTS) ||
            (this.taskType === TaskType.SEARCH_BY_PROFILE) ||
            (this.taskType === TaskType.VARIATIONS_REPORT) ||
            (this.taskType === TaskType.NEAR_DUPE) ||
            this.task === SearchType.SEARCH_FROM_BULK_SEARCH ||
            this.task === SearchType.METADATA_BULK ||
            this.task === SearchType.VECTOR_SET_FIND_DUPLICATES ||
            this.task === SearchType.COLLECTION_SIMILARITY ||
            this.task === SearchType.DOCUMENT_FIND_DUPLICATES;
    }

    isDownloadType(): boolean {
        return (
            (this.operationState === OperationState.WARNING) ||
            (this.taskType === TaskType.VIEW_MANIFEST_DOWNLOAD) ||
            (this.taskType === TaskType.EXPORT_STREAM_CUSTODIAN_UPDATE_DOWNLOAD) ||
            (this.taskType === TaskType.COMPARE_EXPORT) ||
            (this.taskType === TaskType.DOWNLOAD_DOCUMENTS) ||
            (this.task === TaskType.GENERATE_CLUSTER_REPORT) ||
            (this.task === TaskType.DOMAIN_COMMUNICATION) ||
            (this.task === TaskType.EMAIL_COMMUNICATION) ||
            (this.task === 'PROJECT_BILLING_REPORT') ||
            (this.task === 'DATA_PROCESSING_REPORT') ||
            (this.task === 'TERM_SEARCH_HIT_COUNT') ||
            (this.task === 'SOURCE_TRACKING_REPORT') ||
            (this.task === 'SLACK_DATA_REPORT') || 
            ((this.taskType === TaskType.OTHER) && this.warningDetails && this.warningDetails.url) ||
            (this.task === 'COLLECTION_PRODUCTION_SET_CREATE' && this.taskStatus === TaskStatus.FAILURE && this.warnings)
        ) &&
            this.operationState !== OperationState.CANCELLED;
    }

    isDeleteable(): boolean {
        return (this.taskStatus === TaskStatus.SUCCESS) ||
            (this.taskStatus === TaskStatus.CANCELLED) ||
            (this.operationState === OperationState.WARNING) ||
            (this.taskStatus === TaskStatus.FAILURE);
    }
}

export class JobHistoryTask extends DrEntity {
    accessAllowed: boolean;
    dateCompleted: string;
    dateStarted: string;
    description: string;
    operationState: OperationState;
    operationType: string;
    taskType: string;
    orgName: string;
    owner: string;
    projectHandle: string;
    projectName: string;
    secondsElapsed: string;
    status: string;
    systemError: SystemError;
    workbasketHandle: string;

    iconClass: string;
    iconTitle: string;
    durationNoCounter = true;
    warningDetails: any;
    warnings: boolean;
    isDownloadableTask: boolean;

    constructor(value?: Partial<JobHistoryTask>) {
        super();
        if (value) {
            Object.assign(this, value);
            if (value.systemError) {
                this.systemError = new SystemError(value.systemError);
                this.orgName = value.systemError.orgName;
                this.description = value.systemError.operation;
                this.projectName = value.systemError.projectName;
                this.owner = value.systemError.userName;
            }
            if (value.dateStarted) {
                const dateStartedParts = value.dateStarted.split('-');
                if (dateStartedParts.length > 5) {
                    this.dateStarted = dateStartedParts[0] + '-' + dateStartedParts[1] + '-' + dateStartedParts[2] + ' ' + dateStartedParts[3] + ':' + dateStartedParts[4] + ':' + dateStartedParts[5];
                }
            }
            if (value.dateCompleted) {
                const dateCompletedParts = value.dateCompleted.split('-');
                if (dateCompletedParts.length > 5) {
                    this.dateCompleted = dateCompletedParts[0] + '-' + dateCompletedParts[1] + '-' + dateCompletedParts[2] + ' ' + dateCompletedParts[3] + ':' + dateCompletedParts[4] + ':' + dateCompletedParts[5];
                }
            }

            this.isDownloadableTask = this.isDownloadType();
            this.iconClass = getIconClassFromTaskStatus(this.status === 'CANCELED' ? TaskStatus.CANCELLED : this.status as TaskStatus, this.operationState);
            this.iconTitle = getIconTitleForIconClass(this.iconClass, this.operationState);
        }
    }

    isDownloadType(): boolean {
        return (this.operationState === OperationState.WARNING) ||
            (this.operationType === TaskType.VIEW_MANIFEST_DOWNLOAD) ||
            (this.operationType === TaskType.EXPORT_STREAM_CUSTODIAN_UPDATE_DOWNLOAD) ||
            (this.operationType === TaskType.COMPARE_EXPORT) ||
            (this.operationType === TaskType.DOWNLOAD_DOCUMENTS) ||
            (this.operationType === TaskType.GENERATE_CLUSTER_REPORT) ||
            (this.operationType === TaskType.DOMAIN_COMMUNICATION) ||
            (this.operationType === TaskType.EMAIL_COMMUNICATION) ||
            (this.operationType === 'PROJECT_BILLING_REPORT') ||
            (this.operationType === 'DATA_PROCESSING_REPORT') ||
            (this.operationType === 'TERM_SEARCH_HIT_COUNT') ||
            (this.operationType === 'SOURCE_TRACKING_REPORT') ||
            ((this.operationType === TaskType.OTHER) && this.warningDetails && this.warningDetails.url) ||
            (this.operationType === 'COLLECTION_PRODUCTION_SET_CREATE' && this.status === TaskStatus.FAILURE && this.warnings);
    }
}

export enum JobHistoryTaskStatusTitle {
    SUCCESS = 'Success',
    WARNING = 'Complete with Warnings/Errors',
    RUNNING = 'Running',
    QUEUED = 'Queued',
    BLOCKED = 'Blocked',
    PAUSING = 'Pausing',
    PAUSED = 'Paused',
    PENDING_CANCELLATION = 'Pending Cancellation',
    CANCELLED = 'Cancelled',
    PAUSED_ON_FAILURE = 'Paused on Failure',
    FAILURE = 'Failure',
    UNKNOWN = 'Unknown',
}

export class SystemError {
    errorCode: string;
    errorMessage: string;
    requestIdentifier: string;
    timeOfFailure: string;
    timeOfFailureFormatted: string;
    workbasketHandle: string;
    operation: string;
    orgName: string;
    userName: string;
    projectName: string;

    constructor(value?: Partial<SystemError>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

export class WorkBasketContext {
    taskFilters: any;
    requestInfoData: any;
    showFilters: boolean;
    allUsersChecked: boolean;
    searchesOnlyChecked: boolean;
    formSubmitted: boolean;
    filterObject: any;

    constructor(value?: Partial<WorkBasketContext>) {
        if (value) {
            Object.assign(this, value);
        }
    }
}

export class SearchTask extends Task {
    colorizedDescription: string;

    constructor(value?: Partial<Task>) {
        super(value);
        if (value) {
            Object.assign(this, value);
        }
    }
}

export class TaskStatusSection {
    name: string;
    data: Array<DrEntityAttribute>;
}

